import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Seo } from "@/lib/useSeo";
import {
  AuthShell,
  PrimaryButton,
  TextField,
  ErrorBox,
  EyeToggle,
} from "@/components/AuthKit";

/**
 * Login — 1:1 port of iOS `src/app/(auth)/login.tsx`.
 * Layout: top-bar back → Y hero (44px) → title "Welcome back" →
 *   subtitle "Log in to continue on BrontoY." → EMAIL + PASSWORD (with
 *   Forgot link and eye toggle) → error box → primary "Log in" pill →
 *   cross-mode "New to BrontoY? Create an account" → legal footer.
 */

const PRIVACY_URL = "https://brontoy.com/privacy";
const GUIDELINES_URL = "https://brontoy.com/guidelines";
const TERMS_URL = "https://brontoy.com/terms";

export default function Login() {
  const navigate = useNavigate();
  const { login, apiError } = useAuth();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const pwRef = useRef(null);

  const submit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (loading) return;
    setErr("");
    setLoading(true);
    try {
      const payload = await login(email.trim(), password);
      if (payload?.next_step === "verify_email" || payload?.user?.email_verified === false) {
        navigate("/verify", { replace: true });
        return;
      }
      // On success, AuthContext.login has already committed authed + welcome
      // splash atomically.  WelcomeGate owns the route transition from
      // here — do NOT navigate manually or the guard/splash will race.
    } catch (e2) {
      const msg = apiError(e2);
      if (/verif/i.test(msg) && /email/i.test(msg)) {
        navigate("/verify", { replace: true });
        return;
      }
      setErr(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      testId="login-screen"
      title="Welcome back"
      subtitle="Log in to continue on BrontoY."
    >
      <Seo
        title="Log in to BrontoY"
        description="Sign in to your BrontoY account to continue sharing moments and expressing your views."
        canonicalPath="/login"
      />
      <form onSubmit={submit} className="flex flex-col gap-4 mt-8">
        <TextField
          id="login-email"
          label="EMAIL"
          type="email"
          autoComplete="email"
          autoCapitalize="none"
          placeholder="you@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              pwRef.current?.focus();
            }
          }}
          testId="login-email-input"
        />

        {/* Password + Forgot? link on the label row */}
        <div className="flex flex-col gap-1.5">
          <div className="flex items-center justify-between">
            <label
              className="text-muted-foreground font-semibold"
              style={{ fontSize: 13, letterSpacing: "0.02em" }}
              htmlFor="login-password"
            >
              PASSWORD
            </label>
            <button
              type="button"
              onClick={() => navigate("/forgot")}
              className="text-[13px] font-semibold hover:opacity-80 transition-opacity"
              style={{ color: "#7638FA" }}
              data-testid="login-forgot-link"
            >
              Forgot?
            </button>
          </div>
          <TextField
            ref={pwRef}
            id="login-password"
            type={showPw ? "text" : "password"}
            autoComplete="current-password"
            placeholder="Your password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            rightAdornment={
              <EyeToggle show={showPw} onToggle={() => setShowPw((s) => !s)} testId="login-toggle-pw" />
            }
            testId="login-password-input"
          />
        </div>

        {err ? <ErrorBox testId="login-error">{err}</ErrorBox> : null}

        <PrimaryButton
          type="submit"
          label="Log in"
          loading={loading}
          className="mt-2"
          testId="login-submit-btn"
        />
      </form>

      {/* New to BrontoY? */}
      <div className="flex items-center justify-center mt-8 text-[14px]">
        <span className="text-muted-foreground">New to BrontoY?&nbsp;</span>
        <button
          type="button"
          onClick={() => navigate("/signup", { replace: true })}
          className="text-foreground font-bold hover:opacity-80 transition-opacity"
          data-testid="login-to-signup"
        >
          Create an account
        </button>
      </div>

      {/* Legal footer — same copy + brand-purple links as landing */}
      <p
        className="text-center text-muted-foreground mt-5"
        style={{ fontSize: 13, lineHeight: "19px" }}
        data-testid="login-legal-text"
      >
        By continuing, you agree to our{" "}
        <a href={PRIVACY_URL} target="_blank" rel="noreferrer" className="font-semibold no-underline hover:opacity-80" style={{ color: "#7638FA" }}>
          Privacy Policy
        </a>
        ,{" "}
        <a href={GUIDELINES_URL} target="_blank" rel="noreferrer" className="font-semibold no-underline hover:opacity-80" style={{ color: "#7638FA" }}>
          Community Guidelines
        </a>
        , and{" "}
        <a href={TERMS_URL} target="_blank" rel="noreferrer" className="font-semibold no-underline hover:opacity-80" style={{ color: "#7638FA" }}>
          Terms of Use
        </a>
        .
      </p>
    </AuthShell>
  );
}
